/**
 * Landscape function library - client-side landscape generation
 * TypeScript port from landscapes.js
 */

export interface LandscapeData {
  x: number[][]
  y: number[][]
  z: number[][]
  x_range: [number, number]
  y_range: [number, number]
}

export interface ManifoldParams {
  // Gaussian wells params
  global_scale?: number
  well_width?: number
  well_depth_scale?: number
  num_wells?: number
  // Ackley params
  a?: number
  b?: number
  c_scale?: number
  vertical_scale?: number
}

/**
 * Generate well positions in a regular polygon pattern
 */
function generateWellPositions(numWells: number, radius = 4.0, baseDepth = 2.5): [number, number, number][] {
  const wells: [number, number, number][] = []
  const n = Math.floor(numWells)
  
  if (n === 0) return wells
  
  if (n === 1) {
    wells.push([0, 0, baseDepth])
  } else if (n === 2) {
    wells.push([0, radius, baseDepth])
    wells.push([0, -radius, baseDepth])
  } else {
    for (let i = 0; i < n; i++) {
      const angle = (2 * Math.PI * i) / n - Math.PI / 2
      const wx = radius * Math.cos(angle)
      const wy = radius * Math.sin(angle)
      wells.push([wx, wy, baseDepth])
    }
  }
  
  return wells
}

/**
 * Custom function with multiple local minima (wells/valleys)
 */
function customMultimodal(
  x: number, 
  y: number, 
  globalScale = 0.1, 
  wellWidth = 3.5, 
  wellDepthScale = -1.5, 
  numWells = 3
): number {
  let loss = (x * x + y * y) * globalScale
  
  const wells = generateWellPositions(numWells, 2.5, 2.5)
  
  for (const [wx, wy, depth] of wells) {
    const distSq = (x - wx) * (x - wx) + (y - wy) * (y - wy)
    loss -= depth * wellDepthScale * Math.exp(-distSq / wellWidth)
  }
  
  loss += 15.0
  return loss
}

/**
 * Ackley function - highly multimodal with deep global minimum
 */
function ackley(x: number, y: number, a = 20, b = 0.2, c_scale = 1.0, vertical_scale = 1.0): number {
  const c = 2 * Math.PI * c_scale
  
  const term1 = -a * Math.exp(-b * Math.sqrt(0.5 * (x * x + y * y)))
  const term2 = -Math.exp(0.5 * (Math.cos(c * x) + Math.cos(c * y)))
  
  return (term1 + term2 + a + Math.E) * vertical_scale
}

type ManifoldFunction = (x: number, y: number, ...args: number[]) => number

const MANIFOLD_FUNCTIONS: Record<string, ManifoldFunction> = {
  'custom_multimodal': customMultimodal,
  'ackley': ackley,
}

/**
 * Generate landscape mesh for a specific manifold
 */
export function generateManifoldLandscape(
  manifoldId: string,
  resolution = 80,
  xRange: [number, number] = [-5, 5],
  yRange: [number, number] = [-5, 5],
  params: ManifoldParams | null = null
): LandscapeData {
  const func = MANIFOLD_FUNCTIONS[manifoldId] || customMultimodal
  
  const [xMin, xMax] = xRange
  const [yMin, yMax] = yRange
  
  const xVals: number[] = []
  const yVals: number[] = []
  
  for (let i = 0; i < resolution; i++) {
    xVals.push(xMin + (xMax - xMin) * i / (resolution - 1))
    yVals.push(yMin + (yMax - yMin) * i / (resolution - 1))
  }
  
  const X: number[][] = []
  const Y: number[][] = []
  const Z: number[][] = []
  
  for (let i = 0; i < resolution; i++) {
    X.push([])
    Y.push([])
    Z.push([])
    
    for (let j = 0; j < resolution; j++) {
      X[i].push(xVals[j])
      Y[i].push(yVals[i])
      
      let zVal: number
      if (params && manifoldId === 'custom_multimodal') {
        zVal = customMultimodal(
          xVals[j], 
          yVals[i],
          params.global_scale ?? 0.1,
          params.well_width ?? 3.5,
          params.well_depth_scale ?? -1.5,
          params.num_wells ?? 3
        )
      } else if (params && manifoldId === 'ackley') {
        zVal = ackley(
          xVals[j],
          yVals[i],
          params.a ?? 20,
          params.b ?? 0.2,
          params.c_scale ?? 1.0,
          params.vertical_scale ?? 1.0
        )
      } else {
        zVal = func(xVals[j], yVals[i])
      }
      
      Z[i].push(zVal)
    }
  }
  
  return { x: X, y: Y, z: Z, x_range: xRange, y_range: yRange }
}

/**
 * Get z value at a specific (x, y) point for a manifold
 */
export function getManifoldZValue(
  manifoldId: string,
  x: number,
  y: number,
  params: ManifoldParams | null = null
): number {
  const func = MANIFOLD_FUNCTIONS[manifoldId] || customMultimodal
  
  if (params && manifoldId === 'custom_multimodal') {
    return customMultimodal(
      x, y,
      params.global_scale ?? 0.1,
      params.well_width ?? 3.5,
      params.well_depth_scale ?? -1.5,
      params.num_wells ?? 3
    )
  }
  
  if (params && manifoldId === 'ackley') {
    return ackley(
      x, y,
      params.a ?? 20,
      params.b ?? 0.2,
      params.c_scale ?? 1.0,
      params.vertical_scale ?? 1.0
    )
  }
  
  return func(x, y)
}
